// Configurações (P0.8): aparência, idioma, Pomodoro, janela flutuante, sons, conta,
// assinatura (Portal do Stripe) e excluir conta (LGPD/GDPR).
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { POMODORO_LIMITS, type BreakMode } from '../domain/pomodoro';
import { LANGUAGES, deleteConfirmWord, resolveLanguage, type Language, type ProfilePatch, type ThemePref, type TimeFormat } from '../domain/settings';
import { tidyName, validateNewPassword } from '../domain/authValidation';
import { useAppState, useStore } from '../state/AppStore';
import { useNav, useServices } from '../state/services';
import { usePomodoro } from '../state/usePomodoro';
import { useAccess } from '../state/useSubscription';
import { formatMinutes } from '../i18n/format';
import { DurationInput, PomodoroSetupDialog, isValidBreak } from './PomodoroSetupDialog';
import { RecurringSection } from './RecurringSection';
import { Icon, type IconName } from './Icon';
import { MinutesInput } from './MinutesInput';
import { cls } from './NewTaskForm';

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="settings__group">
      <h2 className="label-caps">{title}</h2>
      <div className="settings__card">{children}</div>
    </section>
  );
}

function Switch({ label, hint, checked, onChange, disabled }: { label: string; hint?: string; checked: boolean; onChange(v: boolean): void; disabled?: boolean }) {
  return (
    <label className="settings__row">
      <span className="settings__label">
        <span>{label}</span>
        {hint && <small>{hint}</small>}
      </span>
      <input type="checkbox" role="switch" className="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} aria-label={label} />
    </label>
  );
}

/** "Iniciar com o computador" (AC-129): estado real do sistema, só no desktop; vale para este computador. */
function AutostartSwitch() {
  const { t } = useTranslation();
  const { autostart } = useServices();
  const [on, setOn] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    void autostart?.isEnabled().then((v) => alive && setOn(v), () => alive && setOn(false));
    return () => {
      alive = false;
    };
  }, [autostart]);
  if (!autostart || on === null) return null;
  const change = async (v: boolean) => {
    setOn(v);
    try {
      await autostart.setEnabled(v);
    } finally {
      setOn(await autostart.isEnabled().catch(() => !v));
    }
  };
  return <Switch label={t('settings.autostart')} hint={t('settings.autostartHint')} checked={on} onChange={(v) => void change(v)} />;
}

function Segmented<T extends string>({ label, value, options, onChange, disabled }: { label: string; value: T; options: Array<{ value: T; label: string; icon?: IconName }>; onChange(v: T): void; disabled?: boolean }) {
  return (
    <div className="settings__row settings__row--stack">
      <span className="settings__label">{label}</span>
      <div className="segmented" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={value === o.value} className={cls('segmented__item', value === o.value && 'is-on')} disabled={disabled} onClick={() => onChange(o.value)}>
            {o.icon && <Icon name={o.icon} size={14} />}
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Tempo de foco do Pomodoro (AC-231): aceita "190", "3:10" ou "3h10" e mostra "3 h 10 min"; de 1 min a 12 h. */
function FocusField({ value, onSave, disabled }: { value: number; onSave(v: number): void; disabled?: boolean }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<number | null>(value);
  const [error, setError] = useState(false);
  const commit = () => {
    const { min, max } = POMODORO_LIMITS.focus;
    if (pending === null || !Number.isInteger(pending) || pending < min || pending > max) {
      setError(true);
      return;
    }
    setError(false);
    if (pending !== value) onSave(pending);
  };
  return (
    <div className="settings__row">
      <span className="settings__label">
        <span>{t('settings.focusMinutes')}</span>
        {error && <small className="field-error">{t('pomodoro.focusRange')}</small>}
      </span>
      <span onBlur={commit} onKeyDown={(e) => e.key === 'Enter' && commit()}>
        <MinutesInput label={t('settings.focusMinutes')} placeholder={t('newTask.otherPlaceholder')} initialMinutes={value} disabled={disabled} onChange={setPending} />
      </span>
    </div>
  );
}

/** Tempo de pausa do Pomodoro: de 1 segundo a 24 horas (ASM-033). */
function BreakField({ value, onSave, disabled }: { value: number; onSave(v: number): void; disabled?: boolean }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<number | null>(value);
  const [error, setError] = useState(false);
  const commit = () => {
    if (!isValidBreak(pending)) {
      setError(true);
      return;
    }
    setError(false);
    if (pending !== value) onSave(pending);
  };
  return (
    <div className="settings__row">
      <span className="settings__label">
        <span>{t('settings.breakLength')}</span>
        {error && <small className="field-error">{t('pomodoro.durationRange')}</small>}
      </span>
      <DurationInput label={t('settings.breakLength')} initialSec={value} disabled={disabled} onChange={setPending} onBlur={commit} />
    </div>
  );
}

export function SettingsScreen({ onBack }: { onBack(): void }) {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const services = useServices();
  const nav = useNav();
  const profile = useAppState((s) => s.profile);
  const session = useAppState((s) => s.session);
  const online = useAppState((s) => s.online);
  const access = useAccess();
  const [name, setName] = useState(profile?.name ?? '');
  const [pwOpen, setPwOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwDone, setPwDone] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [pomodoroSetup, setPomodoroSetup] = useState(false);
  const { controller: pomodoro } = usePomodoro();
  if (!profile || !session) return null;

  const language: Language = resolveLanguage(profile, [i18n.language]);
  const word = deleteConfirmWord(language);
  const save = (patch: ProfilePatch) => void store.updateProfile(patch);

  const changePassword = async () => {
    const err = validateNewPassword(password);
    if (err) {
      setPwError(t(`errors.auth.${err}`, { min: 6 }));
      return;
    }
    try {
      await store.backend.auth.updatePassword(password);
      setPwError(null);
      setPassword('');
      setPwDone(true);
    } catch {
      setPwError(t('errors.actionFailed'));
    }
  };

  const openPortal = async () => {
    try {
      await services.openExternal(await store.backend.subscription.portalUrl());
    } catch {
      store.toast('errors.portal');
    }
  };

  return (
    <div className="settings" data-testid="settings">
      <header className="settings__head">
        <button type="button" className="icon-btn" aria-label={t('common.back')} onClick={onBack}>
          <Icon name="chevronLeft" size={20} />
        </button>
        <h1 className="screen-title">{t('settings.title')}</h1>
      </header>

      <Group title={t('settings.appearance')}>
        <Segmented<ThemePref>
          label={t('settings.theme')}
          value={profile.theme}
          disabled={!online}
          onChange={(theme) => save({ theme })}
          options={[
            { value: 'light', label: t('settings.themeLight'), icon: 'sun' },
            { value: 'dark', label: t('settings.themeDark'), icon: 'moon' },
            { value: 'system', label: t('settings.themeSystem'), icon: 'monitor' },
          ]}
        />
        <label className="settings__row">
          <span className="settings__label">{t('settings.language')}</span>
          <select
            className="input input--select"
            aria-label={t('settings.language')}
            value={language}
            disabled={!online}
            onChange={(e) => save({ language: e.target.value as Language })}
          >
            {LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {t(`languages.${lang}`)}
              </option>
            ))}
          </select>
        </label>
        <Segmented<TimeFormat>
          label={t('settings.timeFormat')}
          value={profile.time_format}
          disabled={!online}
          onChange={(time_format) => save({ time_format })}
          options={[
            { value: '24h', label: t('settings.time24') },
            { value: '12h', label: t('settings.time12') },
          ]}
        />
      </Group>

      <Group title={t('settings.focus')}>
        <Switch
          label={t('settings.pomodoro')}
          hint={t('settings.pomodoroHint')}
          checked={profile.pomodoro_enabled}
          disabled={!online}
          onChange={(v) => (v ? setPomodoroSetup(true) : void pomodoro.setEnabled(false))}
        />
        <Segmented<BreakMode>
          label={t('pomodoro.whenLabel')}
          value={profile.pomodoro_break_mode}
          disabled={!online}
          onChange={(mode) => save({ pomodoro_break_mode: mode })}
          options={[
            { value: 'block', label: t('pomodoro.modeBlock', { time: formatMinutes(profile.pomodoro_focus_min, t) }) },
            { value: 'task', label: t('pomodoro.modeTask') },
          ]}
        />
        <FocusField value={profile.pomodoro_focus_min} disabled={!online} onSave={(v) => save({ pomodoro_focus_min: v })} />
        <BreakField value={profile.pomodoro_break_sec} disabled={!online} onSave={(v) => save({ pomodoro_break_sec: v })} />
      </Group>

      {/* o que aparece na tela e o que toca (AC-229) */}
      <Group title={t('settings.screen')}>
        <Switch label={t('settings.floatAuto')} hint={t('settings.floatAutoHint')} checked={profile.float_auto_open} disabled={!online} onChange={(v) => save({ float_auto_open: v })} />
        <AutostartSwitch />
        <Switch label={t('settings.sounds')} hint={t('settings.soundsHint')} checked={profile.sounds_enabled} disabled={!online} onChange={(v) => save({ sounds_enabled: v })} />
        <Switch label={t('settings.celebration')} hint={t('settings.celebrationHint')} checked={profile.celebration_enabled} disabled={!online} onChange={(v) => save({ celebration_enabled: v })} />
      </Group>

      <RecurringSection online={online} canEdit={online && access.hasAccess} />

      <Group title={t('settings.account')}>
        <label className="settings__row">
          <span className="settings__label">{t('settings.name')}</span>
          <input className="input input--inline" aria-label={t('settings.name')} placeholder={t('auth.namePlaceholder')} autoComplete="given-name" value={name} maxLength={80} disabled={!online} onChange={(e) => setName(e.target.value)} onBlur={() => tidyName(name) !== profile.name && save({ name: tidyName(name) })} />
        </label>
        <div className="settings__row">
          <span className="settings__label">{t('settings.email')}</span>
          <span className="settings__value">{session.user.email}</span>
        </div>
        <button type="button" className="settings__row settings__row--btn" aria-expanded={pwOpen} onClick={() => setPwOpen(!pwOpen)}>
          <span className="settings__label">{t('settings.changePassword')}</span>
          <Icon name="chevronRight" size={16} />
        </button>
        {pwOpen && (
          <div className="settings__row settings__row--stack">
            <input className="input" type="password" autoComplete="new-password" aria-label={t('settings.newPassword')} placeholder={t('settings.newPassword')} value={password} onChange={(e) => setPassword(e.target.value)} />
            {pwError && <p className="field-error">{pwError}</p>}
            {pwDone && <p className="field-ok">{t('settings.passwordChanged')}</p>}
            <button type="button" className="btn btn--primary btn--sm" disabled={!online} onClick={() => void changePassword()}>
              {t('settings.savePassword')}
            </button>
          </div>
        )}
        <button type="button" className="settings__row settings__row--btn" onClick={() => void store.signOut()}>
          <span className="settings__label">{t('settings.signOut')}</span>
          <Icon name="logout" size={16} />
        </button>
      </Group>

      <Group title={t('settings.subscription')}>
        <div className="settings__row settings__row--nowrap">
          <span className="settings__label">
            <span>{t(access.subscription?.plan === 'annual' ? 'settings.planAnnual' : access.subscription?.plan === 'monthly' ? 'settings.planMonthly' : 'settings.planName')}</span>
            {access.subscription?.current_period_end && access.paying && (
              <small>{t('settings.nextCharge', { date: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(access.subscription.current_period_end)) })}</small>
            )}
          </span>
          <span className={cls('pill', access.paying ? 'pill--green' : 'pill--red')}>
            {access.paying ? t('settings.statusActive') : t('settings.statusEnded')}
          </span>
        </div>
        <div className="settings__row">
          {access.paying ? (
            <button type="button" className="btn btn--outline btn--block" disabled={!online} onClick={() => void openPortal()}>
              <Icon name="external" size={14} />
              {t('settings.manageSubscription')}
            </button>
          ) : (
            <button type="button" className="btn btn--honey btn--block" onClick={() => nav.go('subscription')}>
              {t('trial.subscribe')}
            </button>
          )}
        </div>
      </Group>

      <section className="settings__group">
        <div className="settings__card settings__card--danger">
          <button type="button" className="settings__row settings__row--btn" aria-expanded={deleteOpen} onClick={() => setDeleteOpen(!deleteOpen)}>
            <span className="settings__label">
              <span className="text-danger">{t('settings.deleteAccount')}</span>
              <small>{t('settings.deleteHint')}</small>
            </span>
            <Icon name="trash" size={16} className="text-danger" />
          </button>
          {deleteOpen && (
            <div className="settings__row settings__row--stack" data-testid="delete-account">
              <p className="settings__warn">{t('settings.deleteWarning')}</p>
              <label className="settings__label" htmlFor="delete-confirm">
                {t('settings.deleteType', { word })}
              </label>
              <input id="delete-confirm" className="input" value={confirmText} autoComplete="off" onChange={(e) => setConfirmText(e.target.value)} />
              <button type="button" className="btn btn--danger btn--block" disabled={confirmText.trim().toUpperCase() !== word || !online} onClick={() => void store.deleteAccount()}>
                {t('settings.deleteForever')}
              </button>
            </div>
          )}
        </div>
      </section>
      {pomodoroSetup && <PomodoroSetupDialog onClose={() => setPomodoroSetup(false)} />}
      <p className="settings__version">{t('settings.version', { version: __APP_VERSION__ })}</p>
    </div>
  );
}
